/** Вкладка «Сверка»: начисления (accrual) против баланса ЛК по месяцам и статьям + методика. */
import React from "react";

import type { OzRecon } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { diffText, monthLabel, rub } from "./format";
import { ErrorBox, Loading } from "./shared";
import { moneyClass, tdNum, thClass } from "./ui";

export function ReconTab({
  query,
}: {
  query: { data?: OzRecon; isLoading: boolean; isError: boolean; error: unknown };
}): React.ReactElement {
  const d = query.data;
  return (
    <div className="space-y-4">
      {query.isLoading && <Loading text="Загружаю сверку…" />}
      {query.isError && <ErrorBox error={query.error} what="сверку" />}
      {d && (
        <>
          <div className="overflow-x-auto rounded-md border bg-card">
            <table className="w-full min-w-[640px] text-xs">
              <thead className="bg-muted/40 text-[11px]">
                <tr>
                  <th className={cn(thClass, "text-left")}>Месяц</th>
                  <th className={cn(thClass, "text-left")}>Юрлицо</th>
                  <th className={thClass}>Начисления</th>
                  <th className={thClass}>Баланс ЛК</th>
                  <th className={thClass}>Разница</th>
                  <th className={thClass}>Объяснено</th>
                  <th className={cn(thClass, "text-left")}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {d.months.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-muted-foreground">
                      Сверки за месяцы периода нет: баланс ЛК помесячный, текущий месяц сверяется
                      после его закрытия.
                    </td>
                  </tr>
                )}
                {d.months.map((m) => (
                  <tr key={`${m.month}|${m.legal_name}`} className="border-t">
                    <td className="px-2 py-1">{monthLabel(m.month)}</td>
                    <td className="px-2 py-1">{m.legal_name}</td>
                    <td className={tdNum}>{rub(m.accrual)}</td>
                    <td className={tdNum}>{m.balance === null ? "—" : rub(m.balance)}</td>
                    <td className={tdNum}>{m.diff === null ? "—" : diffText(m.diff)}</td>
                    <td className={cn(tdNum, "text-muted-foreground")}>
                      {m.status === "no_header" ? "—" : diffText(m.explained)}
                    </td>
                    <td className="px-2 py-1">
                      {m.status === "ok" ? (
                        <span className="text-emerald-700 dark:text-emerald-400">✓ сходится</span>
                      ) : m.status === "no_header" ? (
                        <span className="text-muted-foreground">нет баланса ЛК</span>
                      ) : (
                        <span className="font-medium text-red-600 dark:text-red-400">
                          ✗ расхождение{" "}
                          {diffText(Math.abs(m.unexplained) >= 0.005 ? m.unexplained : m.unexplained_abs)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {d.rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border bg-card">
              <table className="w-full min-w-[800px] text-xs">
                <thead className="bg-muted/40 text-[11px]">
                  <tr>
                    <th className={cn(thClass, "text-left")}>Месяц</th>
                    <th className={cn(thClass, "text-left")}>Юрлицо</th>
                    <th className={cn(thClass, "text-left")}>Статья</th>
                    <th className={thClass}>Начисления</th>
                    <th className={thClass}>Баланс ЛК</th>
                    <th className={thClass}>Разница</th>
                    <th className={cn(thClass, "text-left")}>Примечание</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r, i) => {
                    const off = r.diff !== null && Math.abs(r.diff) >= 0.005;
                    return (
                      <tr key={i} className={cn("border-t", r.info && "text-muted-foreground opacity-70")}>
                        <td className="px-2 py-1">{monthLabel(r.month)}</td>
                        <td className="px-2 py-1">{r.legal_name}</td>
                        <td className="px-2 py-1">{r.label}</td>
                        <td className={cn(tdNum, r.accrual !== null && moneyClass(r.accrual))}>
                          {r.accrual === null ? "нет статьи" : rub(r.accrual)}
                        </td>
                        <td className={cn(tdNum, r.balance !== null && moneyClass(r.balance))}>
                          {r.balance === null ? "нет статьи" : rub(r.balance)}
                        </td>
                        <td
                          className={cn(
                            tdNum,
                            off &&
                              !r.info &&
                              (r.note
                                ? "text-amber-700 dark:text-amber-400"
                                : "font-medium text-red-600 dark:text-red-400"),
                          )}
                        >
                          {r.diff === null ? "—" : diffText(r.diff)}
                        </td>
                        <td className="px-2 py-1 text-xs text-muted-foreground">{r.note ?? ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <Method />
    </div>
  );
}

function Method(): React.ReactElement {
  return (
    <div className="rounded-md border bg-card p-4 text-sm leading-relaxed">
      <h3 className="mb-2 font-semibold">Как посчитано</h3>
      <ul className="list-disc space-y-1.5 pl-5">
        <li>
          <b>Все суммы</b> — начисления Ozon по дням (<code>/v1/finance/accrual/by-day</code>), дата
          = дата начисления. Каждая статья месяца сверяется с балансом ЛК (
          <code>/v1/finance/balance</code>) — таблица выше. Витрину <code>mart.mp_finance_*</code>{" "}
          строит DWH (DAG <code>dwh_build</code>) ежедневно, портал только читает.
        </li>
        <li>
          <b>Выручка, комиссия, логистика, доставка, возвраты, эквайринг</b> — Ozon начисляет по
          отправлению и артикулу: привязка прямая. Сторно продажи и корректировка цены — внутри
          продаж, как в балансе; штуки сторно уменьшают проданное. Склад и кластеры — из
          отправлений.
        </li>
        <li>
          <b>Хранение</b> — по отчёту «Стоимость размещения» по товарам и складам за день.
        </li>
        <li>
          <b>Оплата за клик</b> — по кампании, артикулу и дню; <b>оплата за заказ</b> — по отчёту
          Performance; дни без отчёта — строка кампании «не разбито по SKU».
        </li>
        <li>
          <b>Кросс-докинг</b> — на поставку, внутри поставки по объёму (штуки × литры);{" "}
          <b>обработка при приёмке</b> — по штукам. Кластер — кластер назначения поставки.
        </li>
        <li>
          <b>Страхование</b> — по стоимости остатков дня (штуко-дни × цена).
        </li>
        <li>
          <b>Общие расходы</b> (подписки, техуслуги рекламы, внешнее продвижение, компенсации) — к
          артикулу не привязаны. Переключатель «Общие расходы по выручке» раскладывает их по
          выручке артикулов; итог от этого не меняется. Копейки при разбивке — методом наибольших
          остатков.
        </li>
        <li>
          Метка <b>«распределено»</b> в дереве — вся сумма узла пришла без артикула и разложена по
          правилу; <b>«общие»</b> — не привязаны к артикулу.
        </li>
      </ul>
    </div>
  );
}
